// The legacy UserSettings dialog, opened from the drawer on a desktop.
//
// Personal data greyed out with "contact an administrator" on hover, as the old
// screen had it: the number and the short code drive billing and the provider
// check, so they stay with an administrator. Then the preferences: language,
// the default stand-in and the office. Two things are new against the legacy
// dialog and are drawn in its shape — the mailbox PIN for everyone (the old one
// asked only robot users) and the notification preference, which the old
// dialog carried commented out.

import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  styled,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError, type ProxyLists, type ProxyOption } from "~/api";
import { useBootstrap, useRefresh } from "~/bootstrap";
import { setLanguage, useLang, useT, type Lang } from "~/i18n";

export const VERSION = (import.meta.env.VITE_VERSION as string | undefined) ?? "2.0";

/** The legacy StyledTextField: a standard field whose disabled state still reads. */
export const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: theme.palette.text.disabled,
    color: theme.palette.text.disabled,
  },
  "& .MuiInput-root.Mui-disabled::before": {
    borderBottomStyle: "solid",
    borderBottomColor: theme.palette.text.disabled,
    borderBottomWidth: 1,
  },
  "&& .MuiInput-root.Mui-disabled:hover::before": {
    borderColor: theme.palette.text.disabled,
  },
  "& .MuiFormLabel-root.Mui-focused": {
    color: theme.palette.text.primary,
  },
  "& .MuiFormLabel-root.Mui-disabled": {
    color: theme.palette.text.disabled,
  },
  "& .disabled": {
    color: theme.palette.action.hover,
  },
  "&& .MuiInput-root:hover::before": {
    borderColor: theme.palette.text.disabled,
  },
}));

export const menuProps = {
  MenuProps: {
    sx: {
      "& .MuiMenu-paper": {
        bgcolor: "background.default",
        backgroundImage: "none",
        mt: 0.5,
        width: "85%",
        maxWidth: 355,
      },
    },
  },
};

/** The two pools of stand-ins, in one request. */
export function useProxies() {
  return useQuery({
    queryKey: ["proxies"],
    queryFn: () => api<ProxyLists>("/api/portal/me/proxies"),
    staleTime: 60_000,
  });
}

/** The legacy notification values, spelled as the commented-out field spelled them. */
export const notifyLabel: Record<string, string> = {
  none: "no-notification",
  self: "message-to-me",
  self_and_proxy: "message-to-me-and-rep",
};

/** Keeps a mailbox PIN to digits, as the legacy PIN dialog did. */
export const digits = (s: string) => s.replace(/\s+/g, "").replace(/\D/g, "");

export function UserSettings({ open, onClose }: { open: boolean; onClose: () => void }) {
  const theme = useTheme();
  const abovemd = useMediaQuery(theme.breakpoints.up("md"));
  const belowsm = useMediaQuery(theme.breakpoints.down("sm"));
  const t = useT();
  const lang = useLang();
  const b = useBootstrap();
  const refresh = useRefresh();
  const proxies = useProxies();

  const initial = useMemo(
    () => ({
      favouriteProxyId: b.me.favouriteProxy?.id ?? 0,
      favouriteSecretariatId: b.me.favouriteSecretariat?.id ?? 0,
      notifyDefault: b.me.notifyDefault,
      pin: "",
    }),
    [b.me],
  );
  const [update, setUpdate] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setUpdate(initial);
      setError("");
    }
  }, [open, initial]);

  const unchanged =
    update.favouriteProxyId === initial.favouriteProxyId &&
    update.favouriteSecretariatId === initial.favouriteSecretariatId &&
    update.notifyDefault === initial.notifyDefault &&
    update.pin === "";

  const save = async () => {
    setLoading(true);
    setError("");
    try {
      const profile: Record<string, unknown> = {};
      if (update.favouriteProxyId !== initial.favouriteProxyId) profile.favouriteProxyId = update.favouriteProxyId;
      if (update.favouriteSecretariatId !== initial.favouriteSecretariatId)
        profile.favouriteSecretariatId = update.favouriteSecretariatId;
      if (update.notifyDefault !== initial.notifyDefault) profile.notifyDefault = update.notifyDefault;
      if (Object.keys(profile).length > 0) await api("/api/portal/me", { method: "PATCH", body: profile });
      if (update.pin) await api("/api/portal/me/mailbox-pin", { method: "PUT", body: { pin: update.pin } });
      setUpdate((u) => ({ ...u, pin: "" }));
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : t("something-went-wrong"));
    } finally {
      setLoading(false);
    }
  };

  const users = proxies.data?.users ?? [];
  const secretariats = proxies.data?.secretariats ?? [];
  const chosen = (list: ProxyOption[], id: number) => list.find((o) => o.id === id);

  const field = (label: string, value: string, shrink = false, hideHelperUntilHover = false) => (
    <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
      <Tooltip title={t("contact-an-admin")} followCursor>
        <StyledTextField
          variant="standard"
          label={label}
          helperText=" "
          value={value}
          InputLabelProps={shrink ? { shrink: true } : undefined}
          sx={{
            width: belowsm ? "70%" : "90%",
            ...(hideHelperUntilHover
              ? {
                  "& .MuiFormHelperText-root": { visibility: "hidden" },
                  "&:hover": { "& .MuiFormHelperText-root": { visibility: "visible" } },
                }
              : {}),
          }}
          disabled
        />
      </Tooltip>
    </Grid>
  );

  return (
    <Dialog
      fullScreen={!abovemd}
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: !abovemd ? 0 : 6,
          display: "flex",
          alignItems: "center",
          height: abovemd ? 525 : "100%",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: 2, borderBottomColor: "divider", width: "95%", p: 0, mt: 1 }}>
        <Grid container>
          <Grid item>
            <Grid container>
              <AccountCircleIcon sx={{ color: "text.primary", fontSize: 32, pb: 0.5 }} />
              <Typography color="text.primary" fontSize={20}>
                {t("user-settings")}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent sx={{ width: "95%", p: 0, "::-webkit-scrollbar": { display: "none" } }}>
        <Grid container direction="column">
          <Grid item sx={{ my: 3 }}>
            <Typography color="text.primary" fontSize={14}>
              {t("personal-info")}
            </Typography>
            <Divider sx={{ bgcolor: "text.primary", mb: 3 }} />
            <Grid container justifyContent="center">
              {field("Email", b.me.email || " ")}
              {field(t("first-name"), b.me.firstName || " ")}
              {field(t("surname"), b.me.lastName || " ")}
              {field(t("phone"), b.me.phone || "", true, true)}
              {field(t("vpn-number"), b.me.vpn || "", true, true)}
              <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                <StyledTextField
                  variant="standard"
                  label={t("voicemail-password")}
                  helperText=" "
                  placeholder={b.me.hasMailboxPin ? "••••" : ""}
                  value={update.pin}
                  onChange={(e) => setUpdate({ ...update, pin: digits(e.target.value) })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*", autoComplete: "off" }}
                  sx={{ width: belowsm ? "70%" : "90%" }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Typography color="text.primary" fontSize={14}>
            {t("preferences")}
          </Typography>
          <Divider sx={{ bgcolor: "text.primary", mb: 3 }} />
          <Grid container>
            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
              <StyledTextField
                variant="standard"
                label={t("language")}
                helperText=" "
                value={lang}
                onChange={(e) => setLanguage(e.target.value as Lang)}
                select
                InputLabelProps={{ shrink: true }}
                SelectProps={menuProps}
                sx={{ width: belowsm ? "70%" : "90%" }}
              >
                <MenuItem value="en" style={{ whiteSpace: "normal" }}>
                  {t("english")}
                </MenuItem>
                <MenuItem value="de" style={{ whiteSpace: "normal" }}>
                  {t("german")}
                </MenuItem>
              </StyledTextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
              {users.length < 20 ? (
                <StyledTextField
                  variant="standard"
                  label={t("default-proxy")}
                  helperText=" "
                  value={chosen(users, update.favouriteProxyId) ? update.favouriteProxyId : ""}
                  onChange={(e) => setUpdate({ ...update, favouriteProxyId: Number(e.target.value) })}
                  select
                  InputLabelProps={{ shrink: true }}
                  SelectProps={menuProps}
                  sx={{ width: belowsm ? "70%" : "90%" }}
                >
                  {users.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      <Typography>{r.name}</Typography>
                    </MenuItem>
                  ))}
                </StyledTextField>
              ) : (
                <Autocomplete
                  options={users}
                  getOptionLabel={(o) => o.name}
                  value={chosen(users, update.favouriteProxyId)}
                  onChange={(_, o) => o && setUpdate({ ...update, favouriteProxyId: o.id })}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      variant="standard"
                      label={t("default-proxy")}
                      size="medium"
                      helperText=" "
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  disableClearable
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                  sx={{ width: belowsm ? "70%" : "90%" }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
              <StyledTextField
                variant="standard"
                label={t("virtual-proxy")}
                helperText=" "
                value={chosen(secretariats, update.favouriteSecretariatId) ? update.favouriteSecretariatId : ""}
                onChange={(e) => setUpdate({ ...update, favouriteSecretariatId: Number(e.target.value) })}
                select
                InputLabelProps={{ shrink: true }}
                SelectProps={menuProps}
                sx={{ width: belowsm ? "70%" : "90%" }}
              >
                {secretariats.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    <Typography>{r.name}</Typography>
                  </MenuItem>
                ))}
              </StyledTextField>
            </Grid>
            {b.company.notifyChannel !== "none" ? (
              <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                <StyledTextField
                  variant="standard"
                  label={t("notification-type")}
                  helperText=" "
                  value={update.notifyDefault}
                  onChange={(e) => setUpdate({ ...update, notifyDefault: e.target.value })}
                  select
                  InputLabelProps={{ shrink: true }}
                  SelectProps={menuProps}
                  sx={{ width: belowsm ? "70%" : "90%" }}
                >
                  {Object.entries(notifyLabel).map(([value, key]) => (
                    <MenuItem key={value} value={value} style={{ whiteSpace: "normal" }}>
                      {t(key)}
                    </MenuItem>
                  ))}
                </StyledTextField>
              </Grid>
            ) : null}
          </Grid>
          {error ? (
            <Typography role="alert" sx={{ color: "error.main", fontSize: 14, mt: 1 }}>
              {error}
            </Typography>
          ) : null}
        </Grid>
      </DialogContent>
      <Grid container sx={{ p: 1, pl: 3, pr: belowsm ? 1 : 3, pb: belowsm ? 2 : 1 }}>
        <Typography color="text.secondary" fontSize={12} sx={{ alignSelf: "center" }}>
          Version {VERSION}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            width: belowsm ? "40%" : "auto",
            mr: 1,
            backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main",
            textTransform: "none",
            border: theme.palette.mode === "dark" ? 1 : 0,
            borderColor: theme.palette.mode === "dark" ? "primary.main" : "primary.dark",
            borderRadius: 6,
            "&:hover": { backgroundColor: "primary.dark" },
            "&:disabled": { backgroundColor: "action.disabledBackground", border: 1, borderColor: "action.hover" },
          }}
        >
          <Typography sx={{ color: "primary.contrastText", fontSize: 16 }}>{t("close")}</Typography>
        </Button>
        <LoadingButton
          variant="contained"
          disabled={loading || unchanged}
          loading={loading}
          onClick={save}
          sx={{
            width: belowsm ? "40%" : "auto",
            backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main",
            textTransform: "none",
            border: 1,
            borderColor: unchanged || loading ? "action.hover" : "primary.main",
            borderRadius: 6,
            "&:hover": { backgroundColor: "primary.dark" },
            "&:disabled": { backgroundColor: "action.disabledBackground" },
          }}
        >
          <Typography sx={{ color: unchanged ? "text.primary" : "primary.contrastText", fontSize: 16, visibility: loading ? "hidden" : "visible" }}>
            {t("save")}
          </Typography>
        </LoadingButton>
      </Grid>
    </Dialog>
  );
}
